/**
 * The gateway API seam.
 *
 * Every gateway component and hook imports `gatewayApi` from here — never axios, never
 * `lib/api` directly, so the tenant-slug interceptor and the error envelope behave
 * identically on every gateway call. Paths mirror gateway-api-contract.md exactly.
 *
 * There used to be a second implementation in this file: a `mockGatewayApi` returning
 * `contract.js` fixtures, selected by `REACT_APP_GATEWAY_MOCKS=1`. It existed so the
 * frontend could be built against the contract while the endpoints were still being
 * written. Those endpoints exist now, and a shipped second transport that answers money
 * questions with fixtures — reachable by one stray environment variable — is a
 * liability, not a convenience. Tests mock this module, like every other suite here.
 *
 * A method with no caller is not kept "for later": it is a URL nothing has ever
 * exercised. The endpoints they wrapped are still documented in the contract, and the
 * wrapper is one line to write back against a component that needs it.
 */
import { api, getCurrentTenantSlug } from "../lib/api";
import { isTenantHost } from "../lib/tenantHost";

const unwrap = (p) => p.then((r) => r.data);

// §4.1: the six money-moving super-admin endpoints require a fresh, single-use
// `X-Stepup-Token`. Centralised so every call site attaches it the same way and an
// absent token means an absent header, never an empty-string one.
// EXPORTED (2026-09-16) for the affiliate module's step-up-gated writes. Those four
// endpoints live under `/api/super-admin/affiliate/` and are called through `lib/api`
// with the paths in `affiliate/contract.js`, not through `gatewayApi` — but the TOKEN is
// the gateway's one primitive, minted by the gateway's one endpoint, and there is
// deliberately no affiliate copy of this helper (affiliate contract §4).
export const stepUpHeaders = (stepUpToken) =>
  stepUpToken ? { headers: { "X-Stepup-Token": stepUpToken } } : {};

/**
 * Headers for a write whose identity the CLIENT owns.
 *
 * `POST /tenants/{id}/adjustment` used to mint its own uuid server-side, which meant the
 * ledger's natural key differed on every request and a double-click credited the tenant
 * twice. The key now travels from the screen, minted once per confirmed intent and
 * reused across retries — so a lost response can be retried safely and a second,
 * deliberate credit is a second key.
 */
const writeHeaders = (stepUpToken, idempotencyKey) => {
  const headers = {};
  if (stepUpToken) headers["X-Stepup-Token"] = stepUpToken;
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  return Object.keys(headers).length ? { headers } : {};
};

/**
 * Mint the identity of ONE money-writing intent, for `Idempotency-Key`.
 *
 * Shared by every screen that writes money the client names — adjustments and refunds —
 * because the rule is identical and a second copy is a second chance to get it subtly
 * different. Minted once when the operator CONFIRMS, held until that intent succeeds:
 * every retry of the same intent (a lost response, a re-attempted step-up) is the same
 * write, while a genuinely second action is a second key.
 *
 * `crypto.randomUUID` where available; the fallback exists because jsdom under this
 * project's Jest does not provide it.
 */
export const mintIdempotencyKey = (prefix = "gw") =>
  (globalThis.crypto?.randomUUID?.()
   ?? `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

export const gatewayApi = {
  getBalance: () => unwrap(api.get("/tenant/gateway/balance")),
  listPayouts: () => unwrap(api.get("/tenant/gateway/payouts")),
  requestPayout: (body) => unwrap(api.post("/tenant/gateway/payouts", body)),

  adminListPayments: (params) => unwrap(api.get("/super-admin/gateway/payments", { params })),
  // ONE payment, expanded — the detail dialog. Separate from the list because the list is
  // masked (`schemas.mask_email`) and this is not: reading one buyer's address is a
  // deliberate act, scrolling past four hundred of them is not.
  adminGetPayment: (id) => unwrap(api.get(`/super-admin/gateway/payments/${id}`)),
  // The practices that have taken money, for the payments filter. Served by the gateway's
  // own router under `finance.read` rather than by `/super-admin/tenants`, which is a
  // different permission and answers whole tenant documents.
  adminPaymentTenantOptions: () =>
    unwrap(api.get("/super-admin/gateway/payments/tenant-options")),
  adminPendingPayouts: () => unwrap(api.get("/super-admin/gateway/payouts/pending")),
  adminApprovePayout: (id, stepUpToken) =>
    unwrap(api.post(`/super-admin/gateway/payouts/${id}/approve`, null, stepUpHeaders(stepUpToken))),
  adminRejectPayout: (id, reason, stepUpToken) =>
    unwrap(api.post(`/super-admin/gateway/payouts/${id}/reject`, { reason }, stepUpHeaders(stepUpToken))),
  // The refund's identity is the CLIENT's, for the same reason as an adjustment: the
  // refund id becomes both the ledger's natural key and the Stripe idempotency key, so a
  // retried request that mints a new one sends a SECOND real refund.
  adminRefund: (body, stepUpToken, idempotencyKey) =>
    unwrap(api.post("/super-admin/gateway/refunds", body,
                    writeHeaders(stepUpToken, idempotencyKey))),
  // `run: false` READS the stored report; `run: true` COMPUTES one now. Reading is the
  // default because `useOverviewTriage` polls this on every overview render, while a
  // computation walks a whole day of Stripe balance transactions. The console's one
  // button used to be labelled "Run reconciliation" and send neither — so on any date
  // the nightly job had not covered it re-read nothing, for ever, and said it had run.
  adminReconciliation: (date, { run = false } = {}) =>
    unwrap(api.get(`/super-admin/gateway/reconciliation/${date}`,
                   run ? { params: { run: true } } : undefined)),
  adminTrialBalance: () => unwrap(api.get("/super-admin/gateway/ledger/trial-balance")),
  adminListDisputes: (params) => unwrap(api.get("/super-admin/gateway/disputes", { params })),
  adminDisputeRecovery: (id, mode, stepUpToken) =>
    unwrap(api.post(`/super-admin/gateway/disputes/${id}/recovery`, { mode }, stepUpHeaders(stepUpToken))),
  adminListCommissionRules: (params) => unwrap(api.get("/super-admin/gateway/commission-rules", { params })),
  adminPutCommissionRule: (body) => unwrap(api.put("/super-admin/gateway/commission-rules", body)),
  adminGetSettings: () => unwrap(api.get("/super-admin/gateway/settings")),
  adminPutSettings: (patch, stepUpToken) =>
    unwrap(api.put("/super-admin/gateway/settings", patch, stepUpHeaders(stepUpToken))),
  adminDeadLetterWebhooks: () => unwrap(api.get("/super-admin/gateway/webhooks/dead-letter")),

  // ── the console's own reads (Stage 2) ──────────────────────────────────────
  // `/liability` already aggregates `/balances` server-side (reconcile.liability_report
  // scans the balance docs itself) and answers per-currency `totals` plus every tenant
  // row, so the treasury strip and the balances table are ONE call, not two. Calling both
  // would scan the same collection twice and risk two snapshots that disagree across a
  // concurrent write. NOTE: this endpoint answers snake_case while the rest of the gateway
  // answers camelCase — `super_admin/gateway/shapes.js` is the single place that reconciles
  // the two, and nothing else may read either shape directly.
  adminLiability: () => unwrap(api.get("/super-admin/gateway/liability")),
  // `/balances` is kept for the FILTERED queues only (frozen, in debt), where the filter
  // is the whole point and the server can answer it.
  adminListBalances: (params) => unwrap(api.get("/super-admin/gateway/balances", { params })),
  adminDisputeRates: (windowDays = 90) =>
    unwrap(api.get("/super-admin/gateway/disputes/rates", { params: { windowDays } })),
  adminCreateAdjustment: ({ tenantId, idempotencyKey, ...body }, stepUpToken) =>
    unwrap(api.post(`/super-admin/gateway/tenants/${tenantId}/adjustment`, body,
                    writeHeaders(stepUpToken, idempotencyKey))),

  // §4.1 mint. `proof` is the request BODY, one of `{ code }` (the authorization code
  // from the reused Google code-client flow — what StepUpAuth.jsx actually produces) or
  // `{ credential }` (an ID token). The backend accepts both by name and verifies them
  // the same way /auth/session does before minting a single-use step-up secret. Passing
  // the body rather than a bare string is what keeps the two proofs from being confused:
  // a code sent under the `credential` name is refused by every verifier there is.
  reauth: (proof) => unwrap(api.post("/super-admin/gateway/reauth", proof)),

  getConsultQuote: (id, token) => unwrap(api.get(`/appointments/${id}/quote`, { params: { token } })),
  createConsultIntent: (id, body) => unwrap(api.post(`/consults/${id}/intents`, body)),
  completeConsult: (id) => unwrap(api.post(`/appointments/${id}/complete`, {})),
  noShowConsult: (id, party) => unwrap(api.post(`/appointments/${id}/no-show`, { party })),
  getConsultSession: (id) => unwrap(api.get(`/appointments/${id}/session`)),
  // The PRACTITIONER's own consults (the super-admin twin is `adminListConsults`). Rows
  // are sessions, not rooms: the session is what carries the measured minutes and the
  // captured amount. Filters: `status`, `from`, `to`, `limit`.
  listTenantConsults: (params) => unwrap(api.get("/tenant/consults", { params })),

  // Tenant-owner scoped (`backend/modules/payments/router.py`); the SAME route
  // `admin/payments/StripeTransactions.jsx` already calls inline as
  // `api.post(\`/admin/receipts/${n}/resend\`)`. Centralised here because
  // `TelehealthAdmin.jsx` is a `gatewayApi`-DI component (contract §9) like every other
  // gateway screen, not a direct `lib/api` caller.
  resendReceipt: (receiptNumber) => unwrap(api.post(`/admin/receipts/${receiptNumber}/resend`)),

  getTenantPricing: () => unwrap(api.get("/tenant/pricing")),
  putTenantPricing: (body) => unwrap(api.put("/tenant/pricing", body)),
  heartbeat: (id, token) => unwrap(api.post(`/consults/${id}/heartbeat`, { token })),
  cancelAppointment: (id, body) => unwrap(api.post(`/appointments/${id}/cancel`, body || {})),
  getConsultStatus: (id, token) => unwrap(api.get(`/consults/${id}/status`, { params: { token } })),
  getConsultAccess: (id, token) =>
    unwrap(api.get(`/consults/${id}/access`, token ? { params: { token } } : undefined)),
  // `{}` with an owner session — the practitioner's heartbeat. Role comes from the
  // credential, never the payload.
  practitionerHeartbeat: (id) => unwrap(api.post(`/consults/${id}/heartbeat`, {})),
  // THE DEPARTURE BEACON (contract §2, ADDED 2026-09-03, finding F-16). Fired from
  // `pagehide`, where an ordinary XHR is cancelled with the document — so this goes out
  // through `navigator.sendBeacon`, which the browser keeps alive past the unload.
  //
  // A BEACON CAN SEND A BODY BUT NO HEADERS, which is why the patient's capability
  // travels in the body exactly as it does on `/heartbeat`; the practitioner sends `{}`
  // and their owner-session cookie rides along on its own (same-origin, and the beacon
  // is a normal credentialed POST). It answers 204 and there is nothing to read back.
  //
  // THE AXIOS FALLBACK IS NOT DEAD CODE: `sendBeacon` is absent under jsdom and returns
  // false when the browser's background-transfer queue is full, and a departure that was
  // simply dropped is the precision this whole endpoint exists to buy. It is fire and
  // forget in both arms — the bill does not rest on it (`sessions.presence_horizons`
  // already refuses to bill a party who stopped proving they were there), so a rejection
  // here must never surface as an error on a page that is closing.
  leaveConsult: (id, token) => {
    // THE TENANT TRAVELS IN THE PATH, NOT IN A HEADER. `lib/api`'s interceptor scopes
    // every other call with `X-Tenant-Slug` on a platform host, and a beacon cannot carry
    // headers at all — so this uses the middleware's OTHER documented door, the
    // `/api/t/{slug}/` prefix (tenant_middleware.py), whenever the interceptor would have
    // sent the header. On a real tenant host the Host itself resolves the practice and
    // the prefix is deliberately absent, exactly as the header is.
    const slug = isTenantHost() ? null : getCurrentTenantSlug();
    const path = `${slug ? `/t/${slug}` : ""}/consults/${id}/leave`;
    const url = `${api.defaults.baseURL || ""}${path}`;
    const body = JSON.stringify(token ? { token } : {});
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      // The Blob type is what makes FastAPI parse it as JSON; a bare string would arrive
      // as `text/plain;charset=UTF-8` and 422 on a model that forbids extra fields.
      const sent = navigator.sendBeacon(
        url, new Blob([body], { type: "application/json" }));
      if (sent) return Promise.resolve(true);
    }
    return api.post(path, token ? { token } : {}).then(() => true, () => false);
  },

  adminPendingPricing: () => unwrap(api.get("/super-admin/pricing/pending")),
  adminApprovePricing: (id, stepUpToken) =>
    unwrap(api.post(`/super-admin/pricing/${id}/approve`, {}, stepUpHeaders(stepUpToken))),
  adminRejectPricing: (id, reason, stepUpToken) =>
    unwrap(api.post(`/super-admin/pricing/${id}/reject`, { reason }, stepUpHeaders(stepUpToken))),
  adminListConsults: (params) => unwrap(api.get("/super-admin/consults", { params })),
  adminConsultTimeline: (id) => unwrap(api.get(`/super-admin/consults/${id}/timeline`)),
};

export default gatewayApi;
